import { getCountryCodesAction } from "../../../Actions/LogicActions/MyAccount/PersonalInformation/GetCountryCodesAction";
import { getCorporateAction } from "../../../Actions/LogicActions/CorporateAccount/MyCompanyAccount/GetCorporateAction";
import { getCorporateUsersAction } from "../../../Actions/LogicActions/CorporateAccount/MyCompanyAccount/GetCorporateUsersAction";
import { updateCorporateAction } from "../../../Actions/LogicActions/CorporateAccount/MyCompanyAccount/UpdateCorporateAction";
import { updateCorporateUsersAction } from "../../../Actions/LogicActions/CorporateAccount/MyCompanyAccount/UpdateCorporateUsersAction";
import { _eventBus } from "../../../core/events/EventBus";
import { StepDefinition } from "../../../core/flows/StepDefinition";
import { StepFlow } from "../../../core/flows/StepFlow";
import { getTranslatedMessage } from "../../../Lib/Utils/i18n";
import stopManager from "../../../Lib/Utils/StopManager";
import { showNotification } from "../../../Services/notificationService";
import { myCompanyAccountTree } from "../../../Orchestrator/ConstantTree";
import {
    getRecoilStateValue,
    updateRecoilState,
} from "../../../Orchestrator/Recoils/Events/Recoil";
import {
    ICorporateInvoiceDetails,
    ICorporateResponse,
} from "../../../Orchestrator/Types/Interface/ICorporate";
import {
    buildCardRemovalBody,
    buildCorporateUpdateBody,
} from "../../../Components/MyCompanyAccount/corporateView";
import { IEmployeeFormValues } from "../../../Components/MyCompanyAccount/EmployeeFormModal";
import { MyCompanyAccountContext } from "../Context/MyCompanyAccountContext";

interface IEmployeeSubmission {
    values: IEmployeeFormValues;
    memberId: string | null;
}

/** Outcome returned to the modal so it can flag the offending field. */
export interface IEmployeeSubmissionResult {
    ok: boolean;
    duplicateEmail?: boolean;
}

/**
 * The whole My Company Account section runs on one step.
 *
 * MyAccountFlow composes seven sub-flows in an N×N link graph; five panels
 * sharing a single company fetch do not need that. Split a panel out into its
 * own sub-flow if it ever grows its own multi-step journey.
 */
class MyCompanyAccountInitStep extends StepFlow {
    get stepName() { return "MyCompanyAccountInit_Step"; }

    async onEventSetup(): Promise<void> {

        this.registerEventSubscription(
            _eventBus.on<string, void>({
                eventName: myCompanyAccountTree.MyCompanyAccount.Action.Click,
                handler: async ({ data }) => {
                    updateRecoilState("activeCompanyActionAtom", data ?? "");
                },
            })
        );

        this.registerEventSubscription(
            _eventBus.on<ICorporateInvoiceDetails, boolean>({
                eventName:
                    myCompanyAccountTree.MyCompanyAccount.Company.InvoiceDetails.Save,
                handler: async ({ data, reply }) => {
                    if (!data) {
                        reply(false);
                        return;
                    }
                    const corporate =
                        getRecoilStateValue<ICorporateResponse>("corporateDataAtom");
                    // buildCorporateUpdateBody throws when the loaded company has
                    // no address/contract — better than PUTing a body that would
                    // erase them. EventBus dispatches handlers through
                    // queueMicrotask and never awaits them, so an uncaught throw
                    // here would leave Save doing nothing at all, silently.
                    try {
                        updateCorporateAction.setParams({
                            information: buildCorporateUpdateBody(corporate, data),
                        });
                    } catch (error) {
                        console.error("[MyCompanyAccount] Refusing to save:", error);
                        showNotification(
                            "error",
                            getTranslatedMessage("companyUpdateFailed"),
                            ""
                        );
                        reply(false);
                        return;
                    }
                    await updateCorporateAction.execute();
                    reply(Boolean(updateCorporateAction.result));
                },
            })
        );

        this.registerEventSubscription(
            _eventBus.on<void, void>({
                eventName: myCompanyAccountTree.MyCompanyAccount.Employees.Get,
                handler: async () => {
                    await getCorporateUsersAction.execute();
                },
            })
        );

        this.registerEventSubscription(
            // Replies with the real outcome so the form clears only on a
            // genuine success. Inferring it from the roster length would miss
            // an add that succeeds while the roster read is refused — which is
            // the current backend state — and invite a duplicate employee.
            _eventBus.on<IEmployeeSubmission, IEmployeeSubmissionResult>({
                eventName: myCompanyAccountTree.MyCompanyAccount.Employees.Add,
                handler: async ({ data, reply }) => {
                    const values = data?.values;
                    if (!values) {
                        reply({ ok: false });
                        return;
                    }
                    updateCorporateUsersAction.setParams({
                        update: {
                            create: {
                                firstName: values.firstName.trim(),
                                lastName: values.lastName.trim(),
                                email: values.email.trim(),
                                ...(values.phoneNumber
                                    ? { phoneNumber: `${values.countryCode ?? "+971"}${values.phoneNumber}` }
                                    : {}),
                                ...(values.gender ? { gender: values.gender } : {}),
                                ...(values.birthdate
                                    ? { birthdate: values.birthdate.format("YYYY-MM-DD") }
                                    : {}),
                                // Sent unconditionally: these are booleans, and
                                // omitting an unticked box would read as "not
                                // specified" rather than "not allowed".
                                attributes: {
                                    bookingStandardAllowed: values.bookingStandardAllowed === true,
                                    bookingPremiumAllowed: values.bookingPremiumAllowed === true,
                                },
                            },
                        },
                        successMessageKey: "companyEmployeeAdded",
                        successDescriptionKey: "companyEmployeeAddedDetail",
                    });
                    await updateCorporateUsersAction.execute();
                    reply({
                        ok: updateCorporateUsersAction.result === true,
                        duplicateEmail: updateCorporateUsersAction.isDuplicateEmailFailure(),
                    });
                },
            })
        );

        this.registerEventSubscription(
            // PUT .../users `update` carries { id, rank, attributes } in
            // transcity 2.0.5 — rank is required, attributes optional. So
            // editing can change the role AND the booking permissions, but
            // nothing else; the modal disables the rest rather than pretending
            // they save. Role cannot be set at CREATE time: the create block
            // has no rank field at all.
            _eventBus.on<IEmployeeSubmission, IEmployeeSubmissionResult>({
                eventName: myCompanyAccountTree.MyCompanyAccount.Employees.Edit,
                handler: async ({ data, reply }) => {
                    const memberId = data?.memberId;
                    const rank = data?.values?.rank;
                    if (!memberId || !rank) {
                        reply({ ok: false });
                        return;
                    }
                    updateCorporateUsersAction.setParams({
                        update: {
                            update: [
                                {
                                    id: memberId,
                                    rank,
                                    attributes: {
                                        bookingStandardAllowed:
                                            data?.values?.bookingStandardAllowed === true,
                                        bookingPremiumAllowed:
                                            data?.values?.bookingPremiumAllowed === true,
                                    },
                                },
                            ],
                        },
                        successMessageKey: "companyEmployeeUpdated",
                        successDescriptionKey: "companyEmployeeUpdatedDetail",
                    });
                    await updateCorporateUsersAction.execute();
                    reply({ ok: updateCorporateUsersAction.result === true });
                },
            })
        );

        this.registerEventSubscription(
            // Sends `remove` (detach from the team), NOT `delete`. The swagger
            // documents both as uuid arrays with no descriptions, so which one
            // destroys the underlying customer account is unconfirmed. `remove`
            // is the reversible one and it matches the copy the admin actually
            // sees ("removed from your company account"). Do not switch to
            // `delete` until backend confirms the semantics -- Add Employee is
            // 403-blocked, so a wrong guess cannot be undone from this app.
            _eventBus.on<string, boolean>({
                eventName: myCompanyAccountTree.MyCompanyAccount.Employees.Delete,
                handler: async ({ data, reply }) => {
                    if (!data) {
                        // A row whose TeamUser.id came back blank degrades to ""
                        // in toCorporateUsers. Without this the dialog would
                        // close on "Yes" and nothing would happen, silently.
                        showNotification(
                            "error",
                            getTranslatedMessage("companyEmployeeUpdateFailed"),
                            ""
                        );
                        reply(false);
                        return;
                    }
                    updateCorporateUsersAction.setParams({
                        update: { remove: [data] },
                        successMessageKey: "companyEmployeeDeleted",
                        successDescriptionKey: "companyEmployeeDeletedDetail",
                    });
                    await updateCorporateUsersAction.execute();
                    reply(updateCorporateUsersAction.result === true);
                },
            })
        );

        this.registerEventSubscription(
            // Team-scoped enable/disable, keyed by the member's uuid — the id the
            // roster actually returns. transcity 2.0.5 declares `enabled` and
            // `disabled` on TeamUpdateUsers as uuid arrays ("List of user IDs to
            // ENABLE/DISABLE in the team"), on the same PUT that `remove`
            // already uses successfully.
            //
            // This replaces POST/DELETE /customers/{id}/block, which needed a
            // numeric customer id the roster does not return. It is also the
            // better operation on its own terms: the block endpoints are an
            // agent-tier action on the whole CUSTOMER, so they would have
            // suspended the person's entire Etihad Rail account rather than
            // just their access to this company.
            _eventBus.on<{ memberId: string; isActive: boolean }, boolean>({
                eventName: myCompanyAccountTree.MyCompanyAccount.Employees.SetStatus,
                handler: async ({ data, reply }) => {
                    if (!data?.memberId) {
                        showNotification(
                            "error",
                            getTranslatedMessage("companyEmployeeStatusFailed"),
                            ""
                        );
                        reply(false);
                        return;
                    }
                    updateCorporateUsersAction.setParams({
                        update: data.isActive
                            ? { enabled: [data.memberId] }
                            : { disabled: [data.memberId] },
                        successMessageKey: data.isActive
                            ? "companyEmployeeActivated"
                            : "companyEmployeeDeactivated",
                        successDescriptionKey: data.isActive
                            ? "companyEmployeeActivatedDetail"
                            : "companyEmployeeDeactivatedDetail",
                    });
                    await updateCorporateUsersAction.execute();
                    reply(updateCorporateUsersAction.result === true);
                },
            })
        );

        this.registerEventSubscription(
            _eventBus.on<number, boolean>({
                eventName: myCompanyAccountTree.MyCompanyAccount.Cards.Remove,
                handler: async ({ data, reply }) => {
                    if (data == null) {
                        reply(false);
                        return;
                    }
                    const corporate =
                        getRecoilStateValue<ICorporateResponse>("corporateDataAtom");
                    try {
                        updateCorporateAction.setParams({
                            information: buildCardRemovalBody(corporate, data),
                        });
                    } catch (error) {
                        console.error("[MyCompanyAccount] Refusing to remove card:", error);
                        showNotification(
                            "error",
                            getTranslatedMessage("companyUpdateFailed"),
                            ""
                        );
                        reply(false);
                        return;
                    }
                    await updateCorporateAction.execute();
                    reply(Boolean(updateCorporateAction.result));
                },
            })
        );
    }

    async onExecute(): Promise<void> {
        this.setCtx("myCompanyAccountContext", new MyCompanyAccountContext());
        // Addresses carry ISO codes; the country metadata turns them into names
        // and backs the invoice country picker.
        getCountryCodesAction.execute();
        // Employees Bookings resolves stopPlaceRef ids to station names.
        stopManager.fetchStops();
        // The company is NOT fetched here: /customers/me may still be in
        // flight, and corporateId comes from it. The page calls
        // getCorporateAction directly once userData lands -- deliberately not
        // via an event, because EventBus drops an emit that has no subscriber
        // yet and this step registers its handlers asynchronously.
        // Deliberately no next(): like MyAccountInit_Step, this step is the
        // section's event hub and must stay alive for the page's lifetime.
    }
}

export const myCompanyAccountInitStepDeclaration = new StepDefinition({
    stepName: "MyCompanyAccountInit_Step",
    stepClass: MyCompanyAccountInitStep,
});
